import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type ImageIntelReport = {
  scanRunId: string;
  description: string;
  landmarks: Array<{ name: string; confidence: number; detail: string }>;
  logos: Array<{ brand: string; confidence: number; detail: string }>;
  ocrText: string;
  textBlocks: Array<{ text: string; language?: string; note?: string }>;
  locationGuess: Array<{
    place: string;
    confidence: number;
    rationale: string;
    lat?: number;
    lon?: number;
    /** True only when a real gazetteer resolved this place. */
    verified?: boolean;
    verifiedName?: string;
    country?: string;
    sources?: Array<{ label: string; url: string }>;
  }>;
  /** Gazetteer-grounded geospatial layer (OSM, Wikipedia, Overpass, Open-Meteo). */
  geo: {
    exifLocation: { displayName: string | null; lat: number | null; lon: number | null; country: string | null } | null;
    nearby: Array<{ name: string; distanceM: number; url?: string; kind?: string }>;
    elevationM: number | null;
    endpointsUsed: string[];
    notes: string[];
  };
  manipulation: {
    verdict: "likely_authentic" | "inconclusive" | "possible_edit" | "likely_manipulated";
    confidence: number;
    regions: Array<{ area: string; reason: string }>;
    notes: string;
  };
  objects: string[];
  pivots: Array<{ target: string; type: string; reason: string }>;
  similar: Array<{ title: string; url: string; note: string }>;
  reverseSearch: Array<{ engine: string; url: string }>;
  exifSummary: string;
  citations: string[];
  model: string;
  generatedAt: string;
};


const inputSchema = z.object({
  imageDataUrl: z
    .string()
    .regex(/^data:image\/(png|jpe?g|webp|gif|bmp|tiff?);base64,[A-Za-z0-9+/=]+$/, "Unsupported image payload")
    .max(9_000_000, "Image too large — keep under ~6MB"),
  fileName: z.string().max(200).optional(),
  sourceUrl: z.string().url().max(2000).optional(),
  exif: z.record(z.string(), z.union([z.string(), z.number()])).optional(),
  gps: z.object({ lat: z.number(), lon: z.number() }).optional(),
  context: z.string().max(1000).optional(),
  environment: z.enum(["sandbox", "live"]).default("live"),
});

// ---------------------------------------------------------------------------
// Person matching
// ---------------------------------------------------------------------------

export type PersonMatchCandidate = {
  name: string;
  role: string;
  confidence: number;
  /** Model's own self-assessment, kept for transparency but never used as the score. */
  modelConfidence: number;
  independentSources: number;
  verification: "supported" | "likely" | "possible" | "unverified";
  textualNameEvidence: boolean;
  rationale: string;
  matchedSignals: string[];
  sources: Array<{ title: string; url: string; domain: string }>;
  /** Structured hits from free public registries (Wikidata, OpenAlex, GitHub, ...). */
  registry: Array<{ provider: string; name: string; description: string; url: string; attributes: string[] }>;
  suggestedScanTarget: string;

};

export type PersonMatchReport = {
  scanRunId: string;
  descriptor: {
    appearance: string;
    ageRange: string;
    distinguishingFeatures: string[];
    attire: string;
    setting: string;
    visibleText: string[];
    contextClues: string[];
  };
  candidates: PersonMatchCandidate[];
  /** Names that may belong to one person (or a reused photo) — never auto-merged. */
  aliasClusters: Array<{ id: string; labels: string[]; sharedDomains: string[]; confidence: number; evidence: string[] }>;
  /** Exact open-web queries executed, so the search is auditable. */
  searchQueries: string[];
  /** Real pages returned by those queries and handed to the analyst model. */
  webHits: Array<{ title: string; url: string; domain: string; snippet: string }>;
  /** Every free registry/search endpoint actually consulted for this run. */
  endpointsUsed: string[];

  /** Human-readable explanation of what the search did or why it came back empty. */
  note: string;
  reverseSearch: Array<{ engine: string; url: string }>;
  citations: string[];
  caveat: string;
  model: string;
  generatedAt: string;
};


const personMatchSchema = z.object({
  imageDataUrl: z
    .string()
    .regex(/^data:image\/(png|jpe?g|webp|gif|bmp|tiff?);base64,[A-Za-z0-9+/=]+$/, "Unsupported image payload")
    .max(9_000_000, "Image too large — keep under ~6MB"),
  fileName: z.string().max(200).optional(),
  sourceUrl: z.string().url().max(2000).optional(),
  hints: z
    .object({
      name: z.string().max(120).optional(),
      employer: z.string().max(120).optional(),
      country: z.string().max(80).optional(),
      platform: z.string().max(80).optional(),
      context: z.string().max(600).optional(),
    })
    .optional(),
  environment: z.enum(["sandbox", "live"]).default("live"),
});

const MATCH_CAVEAT =
  "This is not biometric face recognition against a face database. Candidates are generated from visual descriptors, " +
  "visible text and open-source search, so they are probabilistic leads that require human verification — never treat " +
  "a candidate as a confirmed identification.";

export const matchPersonPhoto = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => personMatchSchema.parse(d))
  .handler(async ({ data, context }): Promise<PersonMatchReport> => {
    const { requireEntitlement } = await import("@/lib/payments/entitlements.server");
    await requireEntitlement({
      supabase: context.supabase,
      userId: context.userId,
      feature: "ai",
      environment: data.environment,
    });
    const { callGrokVision, callGrok } = await import("./grok.server");

    const hints = data.hints ?? {};
    const hintLines = Object.entries(hints)
      .filter(([, v]) => typeof v === "string" && v.trim())
      .map(([k, v]) => `${k}: ${v}`)
      .join("\n");

    const descSystem =
      "You are IMAGE-INTEL descriptor extraction. Describe the depicted person for open-source investigation purposes: " +
      "apparent age band, build, hair, visible distinguishing features, clothing, uniforms, insignia, badges, jewellery, " +
      "setting, and every piece of readable text (name badges, captions, watermarks, signage). " +
      "Do NOT guess a name from facial features alone. Return STRICT JSON only.";
    const descPrompt = `FILE: ${data.fileName ?? "upload"}
${hintLines ? `ANALYST HINTS:\n${hintLines}\n` : ""}
Return JSON exactly:
{
  "appearance": "2-3 sentence neutral physical description",
  "ageRange": "e.g. 35-45",
  "distinguishingFeatures": ["..."],
  "attire": "clothing, uniform, insignia, badges",
  "setting": "where the photo appears to be taken",
  "visibleText": ["verbatim readable text, including name badges and watermarks"],
  "contextClues": ["event type, organisation cues, era of the photo, photo style"]
}`;

    const descriptorRaw = safeJson(await callGrokVision({ system: descSystem, prompt: descPrompt, imageUrl: data.imageDataUrl })) as any;
    const descriptor: PersonMatchReport["descriptor"] = {
      appearance: typeof descriptorRaw.appearance === "string" ? descriptorRaw.appearance : "",
      ageRange: typeof descriptorRaw.ageRange === "string" ? descriptorRaw.ageRange : "unknown",
      distinguishingFeatures: Array.isArray(descriptorRaw.distinguishingFeatures) ? descriptorRaw.distinguishingFeatures.map(String).slice(0, 12) : [],
      attire: typeof descriptorRaw.attire === "string" ? descriptorRaw.attire : "",
      setting: typeof descriptorRaw.setting === "string" ? descriptorRaw.setting : "",
      visibleText: Array.isArray(descriptorRaw.visibleText) ? descriptorRaw.visibleText.map(String).slice(0, 20) : [],
      contextClues: Array.isArray(descriptorRaw.contextClues) ? descriptorRaw.contextClues.map(String).slice(0, 12) : [],
    };

    let candidates: PersonMatchCandidate[] = [];
    let citations: string[] = [];
    let aliasClusters: PersonMatchReport["aliasClusters"] = [];
    let webHits: PersonMatchReport["webHits"] = [];
    let queriesRun: string[] = [];
    let endpointsUsed: string[] = [];
    let note = "";

    try {
      const { dedupeSources, scorePhotoMatch, clusterAliases, buildPersonQueries, nameAppearsIn, namesFromVisibleText } =
        await import("@/lib/intel/image-match");
      const { searchOpenWebMulti } = await import("@/lib/intel/websearch.server");
      const { lookupManyPeople } = await import("@/lib/intel/people.server");
      const legibleText = descriptor.visibleText.join(" ").toLowerCase();

      // 1) Build explicit, auditable open-web queries from what the image and
      //    the analyst actually gave us — not from the model's imagination.
      const queries = buildPersonQueries(descriptor, hints);
      const legibleNames = namesFromVisibleText(descriptor.visibleText);
      const seedNames = [...new Set([hints.name ?? "", ...legibleNames].map((n) => n.trim()).filter((n) => n.length >= 3))].slice(0, 3);

      // 2) Query the open web AND the free structured registries in parallel.
      const [searched, seedRegistry] = await Promise.all([
        searchOpenWebMulti(queries, 6),
        lookupManyPeople(seedNames, 3),
      ]);
      queriesRun = searched.queriesRun;
      webHits = searched.hits.slice(0, 30).map((h) => ({ title: h.title, url: h.url, domain: h.domain, snippet: h.snippet }));
      endpointsUsed = [
        ...new Set([
          ...searched.hits.map((h) => h.provider),
          ...[...seedRegistry.values()].flatMap((r) => r.providersWithHits),
        ]),
      ];

      const registryLines = [...seedRegistry.entries()]
        .flatMap(([n, r]) => r.records.map((rec) => `- [${rec.provider}] ${n} → ${rec.name} · ${rec.description} · ${rec.url}${rec.attributes.length ? ` · ${rec.attributes.join("; ")}` : ""}`))
        .slice(0, 30);

      // 3) Hand the real results to the analyst model, which may also run its
      //    own live search, but must cite reachable URLs.
      const res = await callGrok(
        [
          {
            role: "system",
            content:
              "You are an OSINT identity researcher. You are given visual descriptors extracted from a photo, analyst hints, " +
              "REAL web search results and STRUCTURED registry records already retrieved for this case. Identify people who plausibly match. " +
              "Run additional live web and X searches for the names, badge text, employers, events and handles involved. " +
              "Prefer hard textual evidence (names on badges, captions, event pages, staff pages, press photos, profile pages, registry records) " +
              "over appearance similarity. Every candidate MUST cite at least one real, reachable URL — prefer URLs from the " +
              "supplied results or from your own searches; never invent URLs. Return STRICT JSON only.",
          },
          {
            role: "user",
            content: `VISUAL DESCRIPTOR:
${JSON.stringify(descriptor, null, 2)}
${hintLines ? `\nANALYST HINTS:\n${hintLines}` : ""}

QUERIES ALREADY RUN:
${queriesRun.map((q) => `- ${q}`).join("\n") || "- (none)"}

REAL SEARCH RESULTS (title · url · snippet):
${webHits.map((h) => `- ${h.title} · ${h.url} · ${h.snippet}`).join("\n") || "- (no results returned; rely on your own live search)"}

STRUCTURED REGISTRY RECORDS (Wikidata / OpenAlex / GitHub / OpenLibrary / OpenCorporates):
${registryLines.join("\n") || "- (no registry hits)"}

Return JSON: {"candidates":[{"name":"","role":"current role / affiliation","confidence":0-100,"rationale":"why this person matches, referencing the specific source pages","matchedSignals":["which descriptor, badge text or hint supported it"],"sources":[{"title":"","url":""}],"suggestedScanTarget":"best single string to run an OSINT scan on (full name, email, domain or handle)"}],"note":"one sentence on what you searched and how strong the evidence is"}
Return at most 6 candidates, ordered by confidence. If nothing credible is found, return an empty candidates array and explain why in note.`,
          },
        ],
        { liveSearch: true, searchTools: ["web_search", "x_search"] },
      );
      const parsed = safeJson(res.content) as any;
      if (typeof parsed?.note === "string") note = parsed.note.slice(0, 500);

      const rawCandidates = (Array.isArray(parsed.candidates) ? parsed.candidates : []).slice(0, 6);
      // 4) Corroborate every proposed name against the same free registries.
      const candidateRegistry = await lookupManyPeople(
        rawCandidates.map((c: any) => String(c?.name ?? "")),
        6,
      );
      endpointsUsed = [
        ...new Set([...endpointsUsed, ...[...candidateRegistry.values()].flatMap((r) => r.providersWithHits)]),
      ];

      candidates = rawCandidates
        .map((c: any): PersonMatchCandidate => {
          const name = String(c?.name ?? "").slice(0, 160);
          const claimed = Array.isArray(c?.sources) ? c.sources : [];
          // Back-fill from the real result set when the model cited nothing usable.
          const backfill = webHits
            .filter((h) => nameAppearsIn(name, `${h.title} ${h.snippet} ${h.url}`))
            .slice(0, 4)
            .map((h) => ({ title: h.title, url: h.url }));
          const registryRecords = (candidateRegistry.get(name.trim())?.records ?? []).filter((r) =>
            nameAppearsIn(name, r.name) || nameAppearsIn(r.name, name),
          );
          const sources = dedupeSources([
            ...claimed,
            ...backfill,
            ...registryRecords.map((r) => ({ title: `${r.provider}: ${r.name}`, url: r.url })),
          ]);
          const matchedSignals = Array.isArray(c?.matchedSignals) ? c.matchedSignals.map(String).slice(0, 8) : [];
          if (registryRecords.length) {
            matchedSignals.push(`Registry corroboration: ${[...new Set(registryRecords.map((r) => r.provider))].join(", ")}`);
          }
          // Did the image itself carry this person's name (badge, caption, watermark)?
          const textualNameEvidence = name
            .toLowerCase()
            .split(/\s+/)
            .filter((t: string) => t.length >= 3)
            .some((t: string) => legibleText.includes(t));
          const scored = scorePhotoMatch({ sources, matchedSignals, textualNameEvidence });
          return {
            name,
            role: String(c?.role ?? "").slice(0, 200),
            confidence: scored.confidence,
            modelConfidence: Math.max(0, Math.min(100, Number(c?.confidence) || 0)),
            independentSources: scored.independentSources,
            verification: scored.verification,
            textualNameEvidence,
            rationale: String(c?.rationale ?? "").slice(0, 800),
            matchedSignals: matchedSignals.slice(0, 10),
            sources,
            registry: registryRecords.map((r) => ({
              provider: r.provider,
              name: r.name,
              description: r.description,
              url: r.url,
              attributes: r.attributes,
            })),
            suggestedScanTarget: String(c?.suggestedScanTarget ?? c?.name ?? "").slice(0, 180),
          };
        })
        // A candidate with no reachable source is a guess, not a lead.
        .filter((c: PersonMatchCandidate) => c.name && c.sources.length > 0)
        .sort((a: PersonMatchCandidate, b: PersonMatchCandidate) => b.confidence - a.confidence);
      aliasClusters = clusterAliases(candidates.map((c) => ({ name: c.name, sources: c.sources })));
      citations = [...new Set([...res.citations, ...webHits.map((h) => h.url), ...candidates.flatMap((c) => c.registry.map((r) => r.url))])].slice(0, 60);
      if (!candidates.length && !note) {
        note = queriesRun.length
          ? `Ran ${queriesRun.length} open-web quer${queriesRun.length === 1 ? "y" : "ies"} across ${endpointsUsed.length || "several"} free sources and reviewed ${webHits.length} page(s), but no candidate had citable evidence tying a name to this photo.`
          : "No searchable signal could be extracted from the photo — add a suspected name, employer or country hint.";
      }
    } catch (e) {
      note = `Identity search failed: ${e instanceof Error ? e.message : "unknown error"}. The visual descriptor below is still valid.`;
    }


    const report: Omit<PersonMatchReport, "scanRunId"> = {
      descriptor,
      candidates,
      aliasClusters,
      searchQueries: queriesRun,
      webHits,
      endpointsUsed,

      note,
      reverseSearch: reverseSearchLinks(data.sourceUrl),
      citations,
      caveat: MATCH_CAVEAT,
      model: "grok-4.3",
      generatedAt: new Date().toISOString(),
    };



    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: saved, error: saveError } = await supabaseAdmin
      .from("intel_scan_runs")
      .insert({
        target: hints.name || data.fileName || data.sourceUrl || "Person photo match",
        target_type: "image",
        status: "completed",
        risk_score: 0,
        confidence: candidates[0]?.confidence ?? 0,
        source_count: citations.length,
        findings: report,
        entities: candidates.map((c) => ({
          type: "person",
          label: c.name,
          confidence: c.confidence,
          reason: c.rationale,
        })),
        timeline: [],
        raw: { person_match: report, source_url: data.sourceUrl ?? null },
        completed_at: report.generatedAt,
        user_id: context.userId,
        required_clearance: "L1",
        workspace: "image-intelligence",
      })
      .select("id")
      .single();
    if (saveError || !saved) {
      throw new Error(saveError?.message ?? "Person match could not be saved to the Vault");
    }

    return { ...report, scanRunId: saved.id };
  });

type ParsedImageIntel = Partial<Pick<ImageIntelReport,
  | "description"
  | "landmarks"
  | "logos"
  | "ocrText"
  | "textBlocks"
  | "locationGuess"
  | "objects"
  | "pivots"
  | "similar"
>> & {
  manipulation?: Partial<ImageIntelReport["manipulation"]>;
};

function safeJson(text: string): ParsedImageIntel {
  try {
    return JSON.parse(text) as ParsedImageIntel;
  } catch {
    const m = text.match(/\{[\s\S]*\}/);
    if (m) {
      try {
        return JSON.parse(m[0]) as ParsedImageIntel;
      } catch {
        /* ignore */
      }
    }
    return {};
  }
}

function reverseSearchLinks(sourceUrl?: string) {
  if (!sourceUrl) {
    // No public URL for the upload — offer the manual drag-and-drop entry points.
    return [
      { engine: "Google Lens (upload)", url: "https://lens.google.com/upload" },
      { engine: "Yandex Images (upload)", url: "https://yandex.com/images/" },
      { engine: "Bing Visual Search (upload)", url: "https://www.bing.com/visualsearch" },
      { engine: "TinEye (upload)", url: "https://tineye.com/" },
    ];
  }
  const u = encodeURIComponent(sourceUrl);
  return [
    { engine: "Google Lens", url: `https://lens.google.com/uploadbyurl?url=${u}` },
    { engine: "Yandex Images", url: `https://yandex.com/images/search?rpt=imageview&url=${u}` },
    { engine: "Bing Visual Search", url: `https://www.bing.com/images/search?view=detailv2&iss=sbi&q=imgurl:${u}` },
    { engine: "TinEye", url: `https://tineye.com/search?url=${u}` },
  ];
}

export const analyzeImageIntel = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => inputSchema.parse(d))
  .handler(async ({ data, context }): Promise<ImageIntelReport> => {
    const { requireEntitlement } = await import("@/lib/payments/entitlements.server");
    await requireEntitlement({
      supabase: context.supabase,
      userId: context.userId,
      feature: "ai",
      environment: data.environment,
    });
    const { callGrokVision, callGrok } = await import("./grok.server");

    const exifLines = Object.entries(data.exif ?? {})
      .map(([k, v]) => `${k}: ${v}`)
      .join("\n");
    const exifSummary = [
      exifLines || "No EXIF metadata present (likely stripped by a platform or re-encoded).",
      data.gps ? `GPS: ${data.gps.lat}, ${data.gps.lon}` : "",
    ].filter(Boolean).join("\n");

    const system =
      "You are IMAGE-INTEL, an OSINT imagery analyst. Legal, public-source analysis only — never identify private individuals by face. " +
      "Analyse the image forensically: landmarks, architecture, signage, vegetation, license plate formats, road markings, brands/logos, visible text (OCR verbatim), " +
      "and geolocation clues. Also assess digital manipulation (cloning, splicing, inconsistent lighting/shadows/perspective, resampling artifacts, AI-generation tells). " +
      "Be explicit about uncertainty. Return STRICT JSON only, no prose outside JSON.";

    const prompt = `FILE: ${data.fileName ?? "upload"}
${data.context ? `ANALYST CONTEXT: ${data.context}\n` : ""}EXIF METADATA:
${exifSummary}

Return JSON exactly:
{
  "description": "2-4 sentence objective description of the scene",
  "landmarks": [{"name":"","confidence":0-100,"detail":""}],
  "logos": [{"brand":"","confidence":0-100,"detail":"where it appears"}],
  "ocrText": "all readable text, verbatim, newline separated",
  "textBlocks": [{"text":"","language":"","note":"where it appears"}],
  "locationGuess": [{"place":"most specific place name","confidence":0-100,"rationale":"which visual clues","lat":0,"lon":0}],
  "manipulation": {"verdict":"likely_authentic|inconclusive|possible_edit|likely_manipulated","confidence":0-100,"regions":[{"area":"describe region e.g. upper-left sky","reason":""}],"notes":""},
  "objects": ["notable objects/vehicles/uniforms"],
  "pivots": [{"target":"","type":"domain|organization|person|username|location|phone|email","reason":""}]
}`;

    const visionText = await callGrokVision({ system, prompt, imageUrl: data.imageDataUrl });
    const p = safeJson(visionText);

    const landmarks = Array.isArray(p.landmarks) ? p.landmarks : [];
    const logos = Array.isArray(p.logos) ? p.logos : [];
    const locationGuess = Array.isArray(p.locationGuess) ? p.locationGuess : [];
    const ocrText: string = typeof p.ocrText === "string" ? p.ocrText : "";

    // Second pass: live web search to corroborate landmarks/brands/text and surface similar imagery.
    let similar: ImageIntelReport["similar"] = [];
    let citations: string[] = [];
    const leads = [
      ...landmarks.map((landmark) => landmark.name),
      ...logos.map((logo) => logo.brand),
      ...locationGuess.map((location) => location.place),
      ocrText.split("\n").find((l) => l.trim().length > 4),
    ].filter((x): x is string => typeof x === "string" && x.trim().length > 1).slice(0, 6);

    if (leads.length) {
      try {
        const res = await callGrok(
          [
            {
              role: "system",
              content:
                "You are an OSINT researcher verifying image analysis leads with live web search. Return STRICT JSON only.",
            },
            {
              role: "user",
              content: `LEADS EXTRACTED FROM AN IMAGE: ${leads.join(" | ")}
${data.gps ? `EXIF GPS: ${data.gps.lat}, ${data.gps.lon}` : ""}

Search the live web to (a) confirm or refute the identification, (b) find pages hosting the same or visually similar imagery.
Return JSON: {"similar":[{"title":"","url":"","note":"why this matches"}],"confirmations":[{"lead":"","verdict":"confirmed|refuted|unclear","note":""}]}`,
            },
          ],
          { liveSearch: true, searchTools: ["web_search"] },
        );
        const sj = safeJson(res.content);
        similar = Array.isArray(sj.similar) ? sj.similar.slice(0, 12) : [];
        citations = res.citations;
      } catch {
        /* enrichment is best-effort */
      }
    }

    // Ground every proposed place in real gazetteers (OSM / Wikipedia / Overpass).
    // Model-invented coordinates are discarded unless a gazetteer confirms them.
    let geo: ImageIntelReport["geo"] = { exifLocation: null, nearby: [], elevationM: null, endpointsUsed: [], notes: [] };
    let verifiedLocations = locationGuess;
    try {
      const { enrichLocations } = await import("@/lib/intel/geo.server");
      const enrichment = await enrichLocations({
        placeNames: [...locationGuess.map((l) => l.place), ...landmarks.map((l) => l.name)].filter(Boolean),
        gps: data.gps,
      });
      const byQuery = new Map(enrichment.places.map((pl) => [pl.query.toLowerCase(), pl]));
      verifiedLocations = locationGuess.map((l) => {
        const r = byQuery.get(String(l.place ?? "").trim().toLowerCase());
        if (!r || !r.resolved) {
          const { lat: _lat, lon: _lon, ...rest } = l;
          return { ...rest, verified: false, confidence: Math.min(Number(l.confidence) || 0, 45) };
        }
        return {
          ...l,
          verified: true,
          verifiedName: r.displayName ?? undefined,
          country: r.country ?? undefined,
          ...(r.lat != null && r.lon != null ? { lat: r.lat, lon: r.lon } : {}),
          sources: r.sources,
        };
      });
      if (enrichment.exifLocation?.resolved) {
        const ex = enrichment.exifLocation;
        // EXIF GPS beats any visual inference — surface it as the top location.
        verifiedLocations = [
          {
            place: ex.displayName ?? `${ex.lat}, ${ex.lon}`,
            confidence: 96,
            rationale: "Reverse-geocoded from GPS coordinates embedded in the image's own EXIF metadata.",
            lat: ex.lat ?? undefined,
            lon: ex.lon ?? undefined,
            verified: true,
            verifiedName: ex.displayName ?? undefined,
            country: ex.country ?? undefined,
            sources: ex.sources,
          },
          ...verifiedLocations,
        ];
      }
      geo = {
        exifLocation: enrichment.exifLocation
          ? {
              displayName: enrichment.exifLocation.displayName,
              lat: enrichment.exifLocation.lat,
              lon: enrichment.exifLocation.lon,
              country: enrichment.exifLocation.country,
            }
          : null,
        nearby: enrichment.nearby,
        elevationM: enrichment.elevationM,
        endpointsUsed: enrichment.endpointsUsed,
        notes: enrichment.notes,
      };
      citations = [
        ...new Set([
          ...citations,
          ...enrichment.places.flatMap((pl) => pl.sources.map((s) => s.url)),
          ...enrichment.nearby.map((n) => n.url).filter((u): u is string => !!u),
        ]),
      ].slice(0, 60);
    } catch {
      /* geospatial grounding is best-effort */
    }


    const report = {
      description: typeof p.description === "string" ? p.description : "",
      landmarks,
      logos,
      ocrText,
      textBlocks: Array.isArray(p.textBlocks) ? p.textBlocks : [],
      locationGuess: verifiedLocations,
      geo,

      manipulation: {
        verdict: p?.manipulation?.verdict ?? "inconclusive",
        confidence: Number(p?.manipulation?.confidence ?? 0) || 0,
        regions: Array.isArray(p?.manipulation?.regions) ? p.manipulation.regions : [],
        notes: typeof p?.manipulation?.notes === "string" ? p.manipulation.notes : "",
      },
      objects: Array.isArray(p.objects) ? p.objects : [],
      pivots: Array.isArray(p.pivots) ? p.pivots : [],
      similar,
      reverseSearch: reverseSearchLinks(data.sourceUrl),
      exifSummary,
      citations,
      model: "grok-4.3",
      generatedAt: new Date().toISOString(),
    };

    const adverseVerdict = report.manipulation.verdict === "possible_edit"
      || report.manipulation.verdict === "likely_manipulated";
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: saved, error: saveError } = await supabaseAdmin
      .from("intel_scan_runs")
      .insert({
        target: data.fileName || data.sourceUrl || "Uploaded image",
        target_type: "image",
        status: "completed",
        risk_score: adverseVerdict ? report.manipulation.confidence : 0,
        confidence: Math.max(
          report.manipulation.confidence,
          ...report.locationGuess.map((location) => location.confidence),
          0,
        ),
        source_count: report.citations.length + (data.exif ? 1 : 0),
        findings: report,
        entities: report.pivots.map((pivot) => ({
          type: pivot.type,
          label: pivot.target,
          confidence: 70,
          reason: pivot.reason,
        })),
        timeline: [],
        raw: { image_report: report, source_url: data.sourceUrl ?? null },
        completed_at: report.generatedAt,
        user_id: context.userId,
        required_clearance: "L1",
        workspace: "image-intelligence",
      })
      .select("id")
      .single();
    if (saveError || !saved) {
      throw new Error(saveError?.message ?? "Image report could not be saved to the Vault");
    }

    return { ...report, scanRunId: saved.id };
  });